/**
 * Cookie Preferences Modal
 *
 * Modal dialog for managing cookie consent preferences.
 * Allows users to toggle analytics and marketing cookies.
 *
 * Business Rules:
 * - Essential cookies shown as "always on" (cannot be disabled)
 * - Analytics and Marketing can be toggled independently
 * - When opened via "Do Not Sell" link, marketing is forced off and disabled
 * - GPC (Global Privacy Control) overrides marketing to false
 * - Changes only saved when "Save" is clicked
 *
 * Accessibility:
 * - Mantine Modal handles focus trap and escape key
 * - Switches have clear labels and descriptions
 * - Sufficient touch targets on mobile
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Switch,
  Stack,
  Group,
  Button,
  Paper,
  Alert,
  Divider,
} from '@mantine/core';
import { IconInfoCircle, IconLock } from '@tabler/icons-react';
import { useConsent, useConsentModal } from '../../contexts/ConsentContext';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import { brandColors, semanticColors } from '../../config/theme';
import { CONSENT_UI_TEXT } from '../../constants/consent';
import { AppModal } from './AppModal';

/**
 * Cookie preferences modal component.
 * Rendered once in the app and controlled via ConsentContext.
 */
export const CookiePreferencesModal: React.FC = () => {
  const { consent, isGpcEnabled, setConsent } = useConsent();
  const { isOpen, doNotSellMode, close } = useConsentModal();
  const zIndex = useModalZIndex(isOpen);
  const navigate = useNavigate();

  // Local state for form values (not saved until user clicks Save)
  const [analytics, setAnalytics] = useState(consent.analytics);
  const [marketing, setMarketing] = useState(consent.marketing);

  // Reset form values when modal opens
  useEffect(() => {
    if (isOpen) {
      setAnalytics(consent.analytics);
      
      // In "Do Not Sell" mode, force marketing off
      if (doNotSellMode) {
        setMarketing(false);
      } else {
        setMarketing(consent.marketing);
      }
    }
  }, [isOpen, consent.analytics, consent.marketing, doNotSellMode]);

  /**
   * Handle save - persist consent and close modal.
   */
  const handleSave = () => {
    setConsent({
      analytics,
      // In Do Not Sell mode or with GPC, marketing is forced off
      marketing: doNotSellMode || isGpcEnabled ? false : marketing,
    });
  };

  /**
   * Handle cancel - close without saving.
   */
  const handleCancel = () => {
    close();
  };

  /**
   * Navigate to Privacy Policy and close the modal.
   *
   * Business rule:
   * - Users should be able to access the full Privacy Policy from the consent UI (best practice).
   */
  const handleOpenPrivacyPolicy = () => {
    close();

    navigate('/privacy');
  };

  // Marketing is disabled when GPC is enabled or in "Do Not Sell" mode
  const isMarketingDisabled = isGpcEnabled || doNotSellMode;

  return (
    <AppModal
      id="cookie-preferences"
      opened={isOpen}
      onClose={handleCancel}
      title={CONSENT_UI_TEXT.modalTitle}
      size="md"
      centered
      zIndex={zIndex}
      bodyHeight="auto"
      styles={{ title: { fontSize: '1.25rem', fontWeight: 600 } }}
      footer={
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleCancel}>
            {CONSENT_UI_TEXT.cancel}
          </Button>
          <Button onClick={handleSave}>
            {CONSENT_UI_TEXT.save}
          </Button>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* Description */}
        <Text size="sm" c={semanticColors.text.secondary}>
          {CONSENT_UI_TEXT.modalDescription}
        </Text>

        <Text size="sm" c={semanticColors.text.secondary}>
          Learn more in our{' '}
          <Text
            component="button"
            type="button"
            onClick={handleOpenPrivacyPolicy}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--mantine-color-blue-6)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Privacy Policy
          </Text>
          .
        </Text>

        {/* GPC Notice - browser has Global Privacy Control enabled */}
        {isGpcEnabled && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color={brandColors.info}
            variant="light"
          >
            {CONSENT_UI_TEXT.gpcNotice}
          </Alert>
        )}

        <Divider />

        {/* Cookie Categories */}
        <Stack gap="md">
          {/* Essential - Always On */}
          <Paper p="sm" radius="sm" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={4} style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text fw={500} size="sm">
                    {CONSENT_UI_TEXT.essentialLabel}
                  </Text>
                  <IconLock size={14} color="var(--mantine-color-dimmed)" />
                </Group>
                <Text size="xs" c={semanticColors.text.secondary}>
                  {CONSENT_UI_TEXT.essentialDescription}
                </Text>
              </Stack>
              <Switch
                checked={true}
                disabled
                aria-label="Essential cookies (always enabled)"
              />
            </Group>
          </Paper>

          {/* Analytics */}
          <Paper p="sm" radius="sm" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={4} style={{ flex: 1 }}>
                <Text fw={500} size="sm">
                  {CONSENT_UI_TEXT.analyticsLabel}
                </Text>
                <Text size="xs" c={semanticColors.text.secondary}>
                  {CONSENT_UI_TEXT.analyticsDescription}
                </Text>
              </Stack>
              <Switch
                checked={analytics}
                onChange={(e) => setAnalytics(e.currentTarget.checked)}
                aria-label="Analytics cookies"
              />
            </Group>
          </Paper>

          {/* Marketing */}
          <Paper p="sm" radius="sm" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={4} style={{ flex: 1 }}>
                <Text fw={500} size="sm">
                  {CONSENT_UI_TEXT.marketingLabel}
                </Text>
                <Text size="xs" c={semanticColors.text.secondary}>
                  {CONSENT_UI_TEXT.marketingDescription}
                </Text>
                {isGpcEnabled && (
                  <Text size="xs" c={brandColors.info} fw={500}>
                    Disabled by your browser's Global Privacy Control setting
                  </Text>
                )}
              </Stack>
              <Switch
                checked={isMarketingDisabled ? false : marketing}
                onChange={(e) => setMarketing(e.currentTarget.checked)}
                disabled={isMarketingDisabled}
                aria-label="Marketing cookies"
              />
            </Group>
          </Paper>
        </Stack>

        {/* California privacy notice - subtle text for transparency */}
        <Text size="xs" c={semanticColors.text.secondary} ta="center">
          {CONSENT_UI_TEXT.privacyNotice}
        </Text>
      </Stack>
    </AppModal>
  );
};
